import React, { useCallback, type PropsWithChildren } from 'react';
import { styled } from '@compiled/react';
import { ButtonGroup } from '@atlaskit/button';
import { Flex, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { getWillShowNav3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav3.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useHasToolSections } from '@atlassian/jira-software-connect-toolbar/src/controllers/index.tsx';
import { BoardConnectToolbar } from '@atlassian/jira-software-connect-toolbar/src/ui/board-connect-toolbar/index.tsx';
import { ImportWorkButton } from '@atlassian/jira-software-onboarding-invites/src/ui/import-work-button/async.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useIsEmbedView } from '../../../common/hooks/use-is-embed-view/index.tsx';
import type { PanelType } from '../../../model/panels/types.tsx';
import { togglePanel } from '../../../state/actions/panels/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../state/index.tsx';
import {
	getIsCompanyManaged,
	getProjectKey,
	getRapidViewId,
} from '../../../state/selectors/app/index.tsx';
import { operationsSelector } from '../../../state/selectors/extension/index.tsx';
import { isBacklogWithSprints } from '../../../state/selectors/sprints/index.tsx';
import { useIsInsightsPanelOpen } from '../../../state/state-hooks/use-is-insights-panel-open.tsx';
import { backlogConnectAppLocations } from '../constants.tsx';
import Operations from '../extension/index.tsx';
import Menu from '../menu/index.tsx';
import { FullscreenButton } from './fullscreen-button/index.tsx';
import { InsightsButton } from './insights-button/index.tsx';
import { ShareButton } from './share-button/index.tsx';
import BacklogViewSettings from './view-settings/index.tsx';

export type Props = {
	isMenuVisible: boolean;
	boardType?: string;
};

const ConditionalButtonGroup = ({
	isVisuallyRefreshed,
	children,
}: PropsWithChildren<{ isVisuallyRefreshed: boolean }>) => {
	if (isVisuallyRefreshed) {
		return children;
	}

	return <ButtonGroup>{children}</ButtonGroup>;
};

const ActionsBarNew = ({ isMenuVisible }: Props) => {
	const isEmbedView = useIsEmbedView();
	const rapidViewId = useBacklogSelector(getRapidViewId);
	const projectKey = useBacklogSelector(getProjectKey);
	const appEditions = useAppEditions();
	const isInsightsOpen = useIsInsightsPanelOpen();
	const backlogWithSprints = useBacklogSelector(isBacklogWithSprints);
	const dispatch = useBacklogDispatch();
	const hasOperations = useBacklogSelector(operationsSelector).length > 0;
	const isConnectAppsVisible =
		useHasToolSections({
			boardId: rapidViewId,
			projectKey,
			mode: 'plan',
			itemLocations: backlogConnectAppLocations,
		}) || hasOperations;

	const shouldShowFullsceenButton = getWillShowNav3();
	const shouldShowMenu =
		!shouldShowFullsceenButton || (isMenuVisible && !isEmbedView) || isConnectAppsVisible;

	const handleOnToggle = useCallback(
		(panelType: PanelType) => {
			dispatch(togglePanel(panelType));
		},
		[dispatch],
	);

	return (
		<Flex
			justifyContent="end"
			wrap="wrap"
			alignItems={isVisualRefreshEnabled() ? 'center' : undefined}
			gap={isVisualRefreshEnabled() ? 'space.100' : undefined}
		>
			<ConditionalButtonGroup isVisuallyRefreshed={isVisualRefreshEnabled()}>
				{!isEmbedView && (
					<>
						<ImportWorkButton />
						{shouldShowFullsceenButton && <FullscreenButton boardId={Number(rapidViewId)} />}
					</>
				)}
				{backlogWithSprints && (
					<InsightsButton
						onToggle={handleOnToggle}
						isInsightsPanelOpen={isInsightsOpen}
						boardId={Number(rapidViewId)}
						edition={appEditions.software}
					/>
				)}
				{/* Remove comment when cleaning up backlog-view-settings-remove-redux-connect */}
				{/* @ts-expect-error Type '{}' is missing the following properties from type 'Props': rapidViewId, isClassic, showEpicAsPanel, isVersionsEnabled, onTogglePanelts(2739) */}
				<BacklogViewSettings />
				{shouldShowMenu && <Menu />}
			</ConditionalButtonGroup>
		</Flex>
	);
};

const ActionsBarOld = ({ isMenuVisible, boardType }: Props) => {
	const isEmbedView = useIsEmbedView();
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const rapidViewId = useBacklogSelector(getRapidViewId);
	const projectKey = useBacklogSelector(getProjectKey);

	const shareContentType = `${boardType}_backlog`;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onShareButtonClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		fireUIAnalytics(analyticsEvent, 'shareContentType', {
			contentType: shareContentType,
		});
	};
	const iconsComponent = (
		<>
			<Operations />
			<ShareButton shareContentType={shareContentType} onTriggerButtonClick={onShareButtonClick} />
			<FullscreenButton boardId={Number(rapidViewId)} />
		</>
	);

	return isEmbedView ? null : (
		<ActionsBarWrapper>
			{isVisualRefreshEnabled() ? (
				<Inline space="space.100">{iconsComponent}</Inline>
			) : (
				<Flex>{iconsComponent}</Flex>
			)}
			{isCompanyManaged && (
				<BoardConnectToolbar boardId={rapidViewId} projectKey={projectKey} mode="plan" />
			)}
			{isMenuVisible ? (
				<MenuWrapper>
					<Menu />
				</MenuWrapper>
			) : null}
		</ActionsBarWrapper>
	);
};

export const ActionsBar = componentWithCondition(
	() => getWillShowNav4(),
	ActionsBarNew,
	ActionsBarOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsBarWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	gap: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuWrapper = styled.div({
	width: 'auto',
});
